import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import './CricAITab.css';

const CricAITab = ({ user, players = [] }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [coachComment, setCoachComment] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalUrl, setVideoModalUrl] = useState('');

  // New request submission state
  const skillOptions = ['Batting', 'LegSpin', 'FastBowling', 'OffSpin', 'WicketKeeping', 'Generic (Fielding)'];
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [newRequest, setNewRequest] = useState({ kidId: '', skill: '', notes: '', file: null });
  const [submittingNewRequest, setSubmittingNewRequest] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/analysis-requests');
      const data = response.data;
      // Combine reports and submissions into one list
      const allRequests = [
        ...(data.submissions || []),
        ...(data.reports || [])
      ];
      setRequests(allRequests);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analysis requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'submitted': { label: 'Submitted', className: 'status-submitted', icon: '📩' },
      'in_progress': { label: 'In Progress', className: 'status-in-progress', icon: '⏳' },
      'pending_coach': { label: 'Pending Coach Review', className: 'status-pending', icon: '👨‍🏫' },
      'review_complete': { label: 'Review Complete', className: 'status-review-complete', icon: '✅' },
      'published': { label: 'Published', className: 'status-published', icon: '📢' },
      'rejected': { label: 'Rejected', className: 'status-rejected', icon: '❌' }
    };
    const config = statusConfig[status] || { label: status, className: 'status-default', icon: '📋' };
    return (
      <span className={`cricai-status-badge ${config.className}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const filteredRequests = requests.filter(req => {
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  });

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setCoachComment(request.coachNotes || '');
    setShowDetailModal(true);
  };

  const handleViewReport = (request) => {
    if (request.reportUrl) {
      setSelectedRequest(request);
      setReportContent(request.reportUrl);
      setShowReportModal(true);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      setUpdating(true);
      setError('');
      await api.put(`/api/analysis-requests/${requestId}`, {
        status: newStatus,
        coach_notes: coachComment
      });
      setSuccess(`Request ${requestId} status updated to ${newStatus}`);
      setShowDetailModal(false);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveCoachNotes = async (requestId) => {
    try {
      setUpdating(true);
      setError('');
      await api.put(`/api/analysis-requests/${requestId}`, {
        coach_notes: coachComment
      });
      setSuccess('Coach comments saved successfully');
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save coach comments');
    } finally {
      setUpdating(false);
    }
  };

  const handlePublish = async (requestId) => {
    if (!coachComment.trim()) {
      setError('Please add coach comments before publishing the report');
      return;
    }
    await handleUpdateStatus(requestId, 'Published');
  };

  const handleReject = async (requestId) => {
    await handleUpdateStatus(requestId, 'Rejected');
  };

  const handleNewRequestFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid video file (MP4, MOV, or AVI)');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('File size must be less than 500MB');
      return;
    }
    setNewRequest(prev => ({ ...prev, file }));
  };

  const handleCloseNewRequestModal = () => {
    setShowNewRequestModal(false);
    setNewRequest({ kidId: '', skill: '', notes: '', file: null });
  };

  const handleSubmitNewRequest = async () => {
    const selectedPlayer = players.find(p => String(p.id) === String(newRequest.kidId));
    if (!selectedPlayer) {
      setError('Please select a player');
      return;
    }
    if (!newRequest.skill) {
      setError('Please select a skill area');
      return;
    }
    if (!newRequest.file) {
      setError('Please upload a video file');
      return;
    }
    try {
      setSubmittingNewRequest(true);
      setError('');

      const now = new Date();
      const dateStr = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
      const extension = newRequest.file.name.split('.').pop();
      const formattedFileName = `${newRequest.skill.replace(/\s+/g, '_')}_${dateStr}.${extension}`;
      const folderName = `${selectedPlayer.id}_${selectedPlayer.first_name}_${selectedPlayer.last_name}`;

      const formData = new FormData();
      formData.append('video', newRequest.file, formattedFileName);
      formData.append('folderName', folderName);
      formData.append('playerId', selectedPlayer.id);
      formData.append('playerName', `${selectedPlayer.first_name} ${selectedPlayer.last_name}`);
      formData.append('skill', newRequest.skill);
      formData.append('notes', newRequest.notes || '');

      const response = await api.post('/api/upload-video-to-drive', formData);

      if (response.data.success) {
        setSuccess('Analysis request submitted successfully!');
        handleCloseNewRequestModal();
        fetchRequests();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit analysis request');
    } finally {
      setSubmittingNewRequest(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getVideoEmbed = (url) => {
    if (!url) return null;
    
    // YouTube URL detection
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      return (
        <iframe
          width="100%"
          height="315"
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          title="Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="cricai-video-embed"
        />
      );
    }
    
    // For blob/direct video URLs - play inline
    return (
      <div className="cricai-video-inline">
        <video controls width="100%" className="cricai-video-player">
          <source src={url} />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cricai-loading">
        <div className="spinner"></div>
        <p>Loading Cric AI analysis requests...</p>
      </div>
    );
  }

  return (
    <div className="cricai-tab">
      {/* Hero Banner */}
      <div className="cricai-hero">
        <div className="cricai-hero-glow" />
        <div className="cricai-hero-content">
          <div className="cricai-hero-left">
            <div className="cricai-hero-badge">🏏 AI Analysis</div>
            <h2 className="cricai-hero-title">Cric AI</h2>
          </div>
          <div className="cricai-hero-mid">
            <div className="cricai-hero-stat">
              <span className="cricai-hero-stat-num">{requests.length}</span>
              <span className="cricai-hero-stat-label">Total</span>
            </div>
            <div className="cricai-hero-divider" />
            <div className="cricai-hero-stat">
              <span className="cricai-hero-stat-num">
                {requests.filter(r => ['submitted', 'in_progress', 'pending_coach'].includes(r.status)).length}
              </span>
              <span className="cricai-hero-stat-label">Pending</span>
            </div>
            <div className="cricai-hero-divider" />
            <div className="cricai-hero-stat">
              <span className="cricai-hero-stat-num">
                {requests.filter(r => r.status === 'review_complete').length}
              </span>
              <span className="cricai-hero-stat-label">Complete</span>
            </div>
            <div className="cricai-hero-divider" />
            <div className="cricai-hero-stat">
              <span className="cricai-hero-stat-num">
                {requests.filter(r => r.status === 'published').length}
              </span>
              <span className="cricai-hero-stat-label">Published</span>
            </div>
          </div>
          <div className="cricai-hero-right">
            <button onClick={() => setShowNewRequestModal(true)} className="cricai-hero-btn" title="New Request">
              ➕ New Request
            </button>
            <button onClick={fetchRequests} className="cricai-hero-btn" title="Refresh">
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span> {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span> {success}
        </div>
      )}

      {/* Filter Bar */}
      <div className="cricai-filter-bar">
        <label>Filter:</label>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Requests</option>
          <option value="submitted">Submitted</option>
          <option value="in_progress">In Progress</option>
          <option value="pending_coach">Pending Coach</option>
          <option value="review_complete">Review Complete</option>
          <option value="published">Published</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Requests Table */}
      <div className="cricai-table-wrapper">
        {filteredRequests.length === 0 ? (
          <div className="cricai-empty">
            <div className="empty-icon">🏏</div>
            <p>No analysis requests found</p>
          </div>
        ) : (
          <table className="cricai-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Video</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => (
                <tr key={req.requestId || req.id} className={`cricai-row status-row-${req.status}`}>
                  <td className="cricai-id">{req.requestId}</td>
                  <td className="cricai-date">{formatDate(req.dateSubmitted)}</td>
                  <td>{getStatusBadge(req.status)}</td>
                  <td>
                    {req.videoUrl ? (
                      <button
                        className="cricai-link cricai-video-btn"
                        onClick={() => { setVideoModalUrl(req.videoUrl); setShowVideoModal(true); }}
                      >
                        🎬 Play
                      </button>
                    ) : (
                      <span className="cricai-na">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail / Review Modal */}
      {showDetailModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content cricai-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🏏 Analysis Request: {selectedRequest.requestId}</h3>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body cricai-modal-body">
              {/* Request Info */}
              <div className="cricai-detail-grid">
                <div className="cricai-detail-item">
                  <label>Player:</label>
                  <span>{selectedRequest.playerName}</span>
                </div>
                <div className="cricai-detail-item">
                  <label>Skill:</label>
                  <span className="cricai-skill-badge">{selectedRequest.skill}</span>
                </div>
                <div className="cricai-detail-item">
                  <label>Status:</label>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div className="cricai-detail-item">
                  <label>Submitted:</label>
                  <span>{formatDate(selectedRequest.dateSubmitted)}</span>
                </div>
                {selectedRequest.submittedBy && (
                  <div className="cricai-detail-item">
                    <label>Submitted By:</label>
                    <span>{selectedRequest.submittedBy}</span>
                  </div>
                )}
                {selectedRequest.assignedCoachName && (
                  <div className="cricai-detail-item">
                    <label>Assigned Coach:</label>
                    <span>{selectedRequest.assignedCoachName}</span>
                  </div>
                )}
              </div>

              {/* User Notes */}
              {selectedRequest.notes && (
                <div className="cricai-section">
                  <h4>📝 User Notes</h4>
                  <div className="cricai-notes-box">{selectedRequest.notes}</div>
                </div>
              )}

              {/* Video */}
              {selectedRequest.videoUrl && (
                <div className="cricai-section">
                  <h4>🎬 Video</h4>
                  {getVideoEmbed(selectedRequest.videoUrl)}
                </div>
              )}

              {/* Report URL */}
              {selectedRequest.reportUrl && (
                <div className="cricai-section">
                  <h4>📄 AI Analysis Report</h4>
                  <div className="cricai-report-actions">
                    <a 
                      href={selectedRequest.reportUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-view-report"
                    >
                      🔗 Open Report in New Tab
                    </a>
                    <button 
                      onClick={() => handleViewReport(selectedRequest)}
                      className="btn-view-report btn-preview"
                    >
                      👁️ Preview Report
                    </button>
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {selectedRequest.summary && (
                <div className="cricai-section">
                  <h4>🤖 AI Summary</h4>
                  <div className="cricai-summary-box">{selectedRequest.summary}</div>
                </div>
              )}

              {/* Coach Comments */}
              <div className="cricai-section">
                <h4>💬 Coach Comments</h4>
                <p className="cricai-hint">
                  Add your review comments. These will be visible to the parent/player once published.
                </p>
                <textarea
                  className="cricai-comment-input"
                  value={coachComment}
                  onChange={(e) => setCoachComment(e.target.value)}
                  placeholder="Enter your review comments, feedback, and recommendations..."
                  rows={5}
                  disabled={selectedRequest.status === 'published'}
                />
                {selectedRequest.status !== 'published' && (
                  <button 
                    className="btn-save-notes"
                    onClick={() => handleSaveCoachNotes(selectedRequest.requestId)}
                    disabled={updating}
                  >
                    {updating ? 'Saving...' : 'Save Comments'}
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              {selectedRequest.status !== 'published' && (
                <div className="cricai-action-buttons">
                  {selectedRequest.status === 'submitted' && (
                    <button
                      className="btn-action btn-start-review"
                      onClick={() => handleUpdateStatus(selectedRequest.requestId, 'PendingCoach')}
                      disabled={updating}
                    >
                      Start Review
                    </button>
                  )}
                  {['submitted', 'in_progress', 'pending_coach'].includes(selectedRequest.status) && (
                    <button
                      className="btn-action btn-mark-complete"
                      onClick={() => handleUpdateStatus(selectedRequest.requestId, 'ReviewComplete')}
                      disabled={updating}
                    >
                      Mark Review Complete
                    </button>
                  )}
                  {['review_complete', 'pending_coach'].includes(selectedRequest.status) && (
                    <button
                      className="btn-action btn-publish"
                      onClick={() => handlePublish(selectedRequest.requestId)}
                      disabled={updating}
                    >
                      Approve & Publish
                    </button>
                  )}
                  <button
                    className="btn-action btn-reject"
                    onClick={() => handleReject(selectedRequest.requestId)}
                    disabled={updating}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Report Preview Modal */}
      {showReportModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content cricai-report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 Report: {selectedRequest.playerName} - {selectedRequest.skill}</h3>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="cricai-report-preview">
                <iframe
                  src={reportContent}
                  title="Analysis Report"
                  className="cricai-report-iframe"
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>
              <div className="cricai-report-footer">
                <a 
                  href={reportContent} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-view-report"
                >
                  🔗 Open in New Tab
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Video Player Modal */}
      {showVideoModal && videoModalUrl && (
        <div className="modal-overlay" onClick={() => { setShowVideoModal(false); setVideoModalUrl(''); }}>
          <div className="modal-content cricai-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎬 Video Player</h3>
              <button className="modal-close" onClick={() => { setShowVideoModal(false); setVideoModalUrl(''); }}>×</button>
            </div>
            <div className="modal-body cricai-video-modal-body">
              {getVideoEmbed(videoModalUrl)}
            </div>
          </div>
        </div>
      )}

      {/* New Request Modal */}
      {showNewRequestModal && (
        <div className="modal-overlay" onClick={handleCloseNewRequestModal}>
          <div className="modal-content cricai-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🏏 New Analysis Request</h3>
              <button className="modal-close" onClick={handleCloseNewRequestModal}>×</button>
            </div>
            <div className="modal-body cricai-modal-body cricai-req-form">
              <div className="cricai-req-field">
                <label>Player</label>
                <select
                  className="cricai-req-control"
                  value={newRequest.kidId}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, kidId: e.target.value }))}
                >
                  <option value="">Choose a player...</option>
                  {players.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>

              <div className="cricai-req-field">
                <label>Skill Area</label>
                <select
                  className="cricai-req-control"
                  value={newRequest.skill}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, skill: e.target.value }))}
                >
                  <option value="">Choose a skill area...</option>
                  {skillOptions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="cricai-req-field">
                <label>Video</label>
                <input
                  type="file"
                  accept="video/mp4,video/mov,video/avi,video/quicktime,video/x-msvideo"
                  className="cricai-req-control cricai-req-file"
                  onChange={handleNewRequestFile}
                />
                {newRequest.file && <p className="cricai-hint">Selected: {newRequest.file.name}</p>}
              </div>

              <div className="cricai-req-field">
                <label>Notes</label>
                <textarea
                  className="cricai-req-control"
                  value={newRequest.notes}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional context for this request..."
                  rows={4}
                />
              </div>
            </div>
            <div className="cricai-req-actions">
              <button className="cricai-req-btn cricai-req-btn-cancel" onClick={handleCloseNewRequestModal} disabled={submittingNewRequest}>Cancel</button>
              <button className="cricai-req-btn cricai-req-btn-submit" onClick={handleSubmitNewRequest} disabled={submittingNewRequest}>
                {submittingNewRequest ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CricAITab;
